import React, { useState, useEffect } from 'react';
import Logo from '../components/common/Logo';
import Sidebar from '../components/dashboard-patient/PatientSidebar';
import { Save, Calendar, Mail, Phone } from 'lucide-react';
import PatientService from '../services/patient.service';
import PatientChangePasswordBox from '../components/auth-patient/PatientChangePasswordBox';
import TimePreferenceComponentPatient from '../components/patient/TimePreferenceComponentPatient';

const PatientProfilePage = () => {
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birth_date: '',
    fiscal_code: '',
    pi: '',             
    gender: '',
    address: '',
    notes: '',
    medical_history: '',
    created_at: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await PatientService.getProfile();
      console.log('Profile data:', data); 
      setProfileData({
        first_name: data.user.first_name || '',
        last_name: data.user.last_name || '',
        email: data.user.email || '',
        phone: data.user.phone || '',
        birth_date: data.user.birth_date || '',
        fiscal_code: data.user.fiscal_code || '',
        pi: data.user.pi || '',      
        gender: data.user.gender || '',
        address: data.user.address || '',
        notes: data.user.notes || '',
        medical_history: data.medical_history || '',
        created_at: data.user.created_at
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (window.confirm('Sei sicuro di voler salvare le modifiche?')) {
      try {
        await PatientService.updateProfile(profileData);
        setSuccess('Profilo aggiornato con successo');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        setError('Errore durante l\'aggiornamento del profilo');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#FAF4EB]">
        <div className="text-xl text-gray-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <Sidebar />
      </div>

      <div className="flex-1 p-8 bg-[#FAF4EB]">
        <div className="flex gap-6">
          {/* Colonna principale sinistra */}
          <div className="flex-1">
            <header className="mb-6">
              <h1 className="text-2xl font-bold">Il Mio Profilo</h1>
              <p className="text-gray-600">Gestisci le tue informazioni personali</p>
            </header>

            {error && (
              <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 mb-4 text-green-700 bg-green-100 rounded-lg" role="alert">
                {success}
              </div>
            )}

            <div>
              {/* Cambio Password */}
              <PatientChangePasswordBox />

              {/* Form Principale */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-4">Informazioni Personali</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome
                    </label>
                    <input
                      type="text"
                      value={profileData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cognome
                    </label>
                    <input
                      type="text"
                      value={profileData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data di Nascita
                    </label>
                    <input
                      type="date"
                      value={profileData.birth_date}
                      onChange={(e) => handleInputChange('birth_date', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Codice Fiscale
                    </label>
                    <input
                      type="text"
                      value={profileData.fiscal_code}
                      onChange={(e) => handleInputChange('fiscal_code', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Partita IVA
                    </label>
                    <input
                      type="text"
                      value={profileData.pi}
                      onChange={(e) => handleInputChange('pi', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Genere
                    </label>
                    <select
                      value={profileData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Seleziona genere</option>
                      <option value="M">Maschio</option>
                      <option value="F">Femmina</option>
                      <option value="OTHER">Non Binario</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Indirizzo
                    </label>
                    <input
                      type="text"
                      value={profileData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note
                    </label>
                    <textarea
                      value={profileData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Storia Medica */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h2 className="text-lg font-semibold mb-4">Storia Medica</h2>
                <textarea
                  value={profileData.medical_history}
                  onChange={(e) => handleInputChange('medical_history', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={4}
                  placeholder="Inserisci qui la tua storia medica..."
                />
              </div>

              {/* Pulsante Salva */}
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salva Modifiche
                </button>
              </div>
            </div>
          </div>

          {/* Colonna destra con i box informativi */}
          <div className="w-80 space-y-4 mt-[92px]">  
            {/* 92px è l'altezza dell'header */}
          {/* Box Data Registrazione */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Registrazione</p>
                  <p className="font-semibold">{formatDate(profileData.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Box Email */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-semibold">{profileData.email}</p>
                </div>
              </div>
            </div>

            {/* Box Telefono */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefono</p>
                  <p className="font-semibold">{profileData.phone || 'Non specificato'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientProfilePage;